import React, { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { useNavigate } from 'react-router';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';

import Loading from '../../../components/Loading';
import Menu from '../../../components/Menu';

import api from '../../../services/api';

import {
    actions,
    controleEstoqueFioReducer,
    initialState
} from './reducer';
import styles from './styles';

const STOCK_FILTER_OPTIONS = [
    { label: 'Mostrar tudo', value: 'todos' },
    { label: 'Apenas em estoque', value: 'em_estoque' },
    { label: 'Estoque zerado', value: 'zerado' },
    { label: 'Com estoque mínimo', value: 'com_estoque_minimo' },
    { label: 'Sem estoque mínimo', value: 'sem_estoque_minimo' },
    { label: 'Abaixo do estoque mínimo', value: 'abaixo_estoque_minimo' },
    { label: 'Solicitação automática ativa', value: 'solicitacao_automatica' },
    { label: 'Sem solicitação automática', value: 'sem_solicitacao_automatica' }
];

const BOOLEAN_OPTIONS = [
    { label: 'Sim', value: true },
    { label: 'Não', value: false }
];

const isSolicitacaoAutomaticaAtiva = (value) => {
    return value === true || value === 1 || value === '1' || value === 'true';
};

const getEstoqueMinimo = (fio) => {
    return Number(fio.estoque_minimo || 0);
};

const getPesoEstoque = (fio) => {
    return Number(fio.peso || fio.quantidade || 0);
};

const ControleEstoqueFio = () => {
    const toast = useRef(null);
    const navigate = useNavigate();
    const [state, dispatch] = useReducer(controleEstoqueFioReducer, initialState);

    const {
        loading,
        executing,
        errorDialogVisible,
        stockDialogVisible,
        stockForm,
        activeIndex,
        consumptionDate,
        consumptionLoading,
        consumptionReport,
        proximaTentativaEm,
        ultimaExecucaoEm,
        statusRotina,
        dataReferencia,
        pesoTotalNecessario,
        totalPendencias,
        totalMetrosProduzidos,
        totalArtigos,
        fios,
        fiosSpi,
        stockFilter,
        erros
    } = state;
    const initialRequestDate = useRef(consumptionDate);
    const initialRequestsLoaded = useRef(false);

    const showToast = useCallback((severity, summary, detail) => {
        if (toast.current) {
            toast.current.show({ severity, summary, detail });
        }
    }, []);

    const showError = useCallback((error) => {
        const detail = error && error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : error && error.message
                ? error.message
                : error;

        showToast('error', 'Ocorreu um Erro', detail);
    }, [showToast]);

    const showSuccess = useCallback((response) => {
        const detail = response && response.data && response.data.message
            ? response.data.message
            : 'Operação realizada com sucesso.';

        showToast('success', 'Sucesso', detail);
    }, [showToast]);

    const stockIndicators = useMemo(() => {
        return fios.reduce((accumulator, fio) => {
            const pesoEstoque = getPesoEstoque(fio);
            const palletWeight = Number(fio.peso_paletes || 0);
            const hasStockWithoutSpi = pesoEstoque > 0 && !fio.spi_id;

            return {
                totalStockWeight: accumulator.totalStockWeight + pesoEstoque,
                totalPalletWeight: accumulator.totalPalletWeight + palletWeight,
                totalDivergence: accumulator.totalDivergence + Math.abs(pesoEstoque - palletWeight),
                inconsistentItems: pesoEstoque !== palletWeight
                    ? accumulator.inconsistentItems + 1
                    : accumulator.inconsistentItems,
                itemsWithoutSpi: hasStockWithoutSpi
                    ? accumulator.itemsWithoutSpi + 1
                    : accumulator.itemsWithoutSpi
            };
        }, {
            totalStockWeight: 0,
            totalPalletWeight: 0,
            totalDivergence: 0,
            inconsistentItems: 0,
            itemsWithoutSpi: 0
        });
    }, [fios]);

    const consumptionIndicators = useMemo(() => {
        return consumptionReport.artigos.reduce((total, item) => {
            return total
                + Number(item.consumotrama1_picanol || 0)
                + Number(item.consumourdume1_picanol || 0)
                + Number(item.consumotrama1_toyota || 0)
                + Number(item.consumourdume1_toyota || 0);
        }, 0);
    }, [consumptionReport.artigos]);

    const fioSpiOptions = useMemo(() => {
        return fiosSpi;
    }, [fiosSpi]);

    const fiosFiltrados = useMemo(() => {
        return fios.filter((fio) => {
            const pesoEstoque = getPesoEstoque(fio);
            const estoqueMinimo = getEstoqueMinimo(fio);
            const hasEstoqueMinimo = estoqueMinimo > 0;
            const solicitacaoAutomaticaAtiva = isSolicitacaoAutomaticaAtiva(fio.solicita_palete_automaticamente);

            // Filtros apenas front-end; a lista original continua vindo do dashboard.
            switch (stockFilter) {
                case 'em_estoque':
                    return pesoEstoque > 0;
                case 'zerado':
                    return pesoEstoque <= 0;
                case 'com_estoque_minimo':
                    return hasEstoqueMinimo;
                case 'sem_estoque_minimo':
                    return !hasEstoqueMinimo;
                case 'abaixo_estoque_minimo':
                    return hasEstoqueMinimo && pesoEstoque < estoqueMinimo;
                case 'solicitacao_automatica':
                    return solicitacaoAutomaticaAtiva;
                case 'sem_solicitacao_automatica':
                    return !solicitacaoAutomaticaAtiva;
                case 'todos':
                default:
                    return true;
            }
        });
    }, [fios, stockFilter]);

    const loadDashboard = useCallback((date = consumptionDate) => {
        dispatch({ type: actions.LOAD_DASHBOARD_START });

        return api.get('estoque/paletes', {
            params: {
                data: date
            }
        })
            .then((response) => {
                dispatch({
                    type: actions.LOAD_DASHBOARD_SUCCESS,
                    payload: response.data || {}
                });
            })
            .catch((error) => {
                showError(error);
            })
            .finally(() => {
                dispatch({ type: actions.LOAD_DASHBOARD_FINISH });
            });
    }, [consumptionDate, showError]);

    const loadRoutineErrors = useCallback((date) => {
        const requestConfig = date
            ? {
                params: {
                    data: date
                }
            }
            : undefined;

        return api.get('estoque/baixa-erros', requestConfig)
            .then((response) => {
                dispatch({
                    type: actions.LOAD_ROUTINE_ERRORS_SUCCESS,
                    payload: response.data || []
                });
            });
    }, []);

    const executeRoutine = useCallback((date) => {
        dispatch({ type: actions.EXECUTE_ROUTINE_START });

        const requestConfig = date
            ? {
                params: {
                    data: date
                }
            }
            : undefined;

        api.get('estoque/executar-rotina', requestConfig)
            .then((response) => {
                showSuccess(response);
                return loadDashboard(date).then(() => loadRoutineErrors(date));
            })
            .catch((error) => {
                showError(error);
            })
            .finally(() => {
                dispatch({ type: actions.EXECUTE_ROUTINE_FINISH });
            });
    }, [loadDashboard, loadRoutineErrors, showError, showSuccess]);

    const handleRefresh = useCallback(() => {
        return loadDashboard(consumptionDate)
            .then(() => loadRoutineErrors(consumptionDate))
            .catch((error) => {
                showError(error);
            });
    }, [consumptionDate, loadDashboard, loadRoutineErrors, showError]);

    const handleSubmitConsumptionReport = useCallback((date = consumptionDate) => {
        if (date === '') {
            showToast('warn', 'Aviso', 'Informe uma data para buscar o consumo de fio.');
            return;
        }

        dispatch({ type: actions.LOAD_CONSUMPTION_REPORT_START });

        api.post('relatorios', {
            data_inicial: date,
            data_final: date,
            tipo: 'consumofio',
            grupos: [],
            turmas: []
        }).then((response) => {
            const responseData = response.data || {};

            dispatch({
                type: actions.LOAD_CONSUMPTION_REPORT_SUCCESS,
                payload: responseData.dados || {}
            });
            showSuccess(response);
        }).catch((error) => {
            showError(error);
        }).finally(() => {
            dispatch({ type: actions.LOAD_CONSUMPTION_REPORT_FINISH });
        });
    }, [consumptionDate, showError, showSuccess, showToast]);

    const handleSubmitStock = useCallback(() => {
        if (stockForm.quantidade === '' || Number(stockForm.quantidade) < 0) {
            showToast('warn', 'Aviso', 'Quantidade não pode ser negativa!');
            return;
        }

        if (Number(stockForm.estoque_minimo || 0) < 0) {
            showToast('warn', 'Aviso', 'Estoque mínimo não pode ser negativo!');
            return;
        }

        dispatch({ type: actions.LOAD_DASHBOARD_START });

        api.post(`estoque/${stockForm.id}/update`, stockForm)
            .then((response) => {
                dispatch({ type: actions.CLOSE_STOCK_DIALOG });
                showSuccess(response);
                loadDashboard();
            })
            .catch((error) => {
                showError(error);
            })
            .finally(() => {
                dispatch({ type: actions.LOAD_DASHBOARD_FINISH });
            });
    }, [loadDashboard, showError, showSuccess, showToast, stockForm]);

    const openStockDialog = (rowData) => {
        dispatch({
            type: actions.OPEN_STOCK_DIALOG,
            payload: {
                ...rowData,
                estoque_minimo: getEstoqueMinimo(rowData),
                solicita_palete_automaticamente: isSolicitacaoAutomaticaAtiva(rowData.solicita_palete_automaticamente)
            }
        });
    };

    const closeStockDialog = () => {
        dispatch({ type: actions.CLOSE_STOCK_DIALOG });
    };

    const openErrorDialog = () => {
        dispatch({ type: actions.OPEN_ERROR_DIALOG });
    };

    const closeErrorDialog = () => {
        dispatch({ type: actions.CLOSE_ERROR_DIALOG });
    };

    const changeStockQuantity = (event) => {
        dispatch({
            type: actions.CHANGE_STOCK_FORM,
            payload: { quantidade: event.target.value }
        });
    };

    const changeStockMinimum = (event) => {
        dispatch({
            type: actions.CHANGE_STOCK_FORM,
            payload: { estoque_minimo: event.value || 0 }
        });
    };

    const changeAutomaticRequest = (event) => {
        dispatch({
            type: actions.CHANGE_STOCK_FORM,
            payload: { solicita_palete_automaticamente: event.value }
        });
    };

    const changeStockSpiId = (event) => {
        dispatch({
            type: actions.CHANGE_STOCK_FORM,
            payload: { spi_id: event.value }
        });
    };

    const changeStockFilter = (event) => {
        dispatch({
            type: actions.CHANGE_STOCK_FILTER,
            payload: event.value
        });
    };

    const changeActiveIndex = (event) => {
        dispatch({
            type: actions.CHANGE_ACTIVE_INDEX,
            payload: event.index
        });
    };

    const changeConsumptionDate = (event) => {
        dispatch({
            type: actions.CHANGE_CONSUMPTION_DATE,
            payload: event.target.value
        });
    };

    const formatNumber = (value, decimals = 2) => {
        return Number(value || 0).toLocaleString('pt-BR', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        });
    };

    const formatCurrency = (value) => {
        return `R$ ${formatNumber(value)}`;
    };

    const formatInteger = (value) => {
        return Number(value || 0).toLocaleString('pt-BR');
    };

    const formatDateTime = (value) => {
        if (!value) {
            return '-';
        }

        if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
            const [year, month, day] = value.substring(0, 10).split('-');
            return `${day}/${month}/${year}`;
        }

        return new Date(value).toLocaleString('pt-BR');
    };

    const renderHeaderTitle = () => {
        if (statusRotina === 'executada') {
            return `Última execução em ${formatDateTime(ultimaExecucaoEm)}`;
        }

        return `Próxima tentativa em ${formatDateTime(proximaTentativaEm)}`;
    };

    const renderSummaryCard = (title, value, description, style) => (
        <div style={{ ...styles.summaryCard, ...style }}>
            <div style={styles.summaryTopline} />
            <div style={styles.summaryTitle}>{title}</div>
            <div style={styles.summaryValue}>{value}</div>
            <div style={styles.summaryDescription}>{description}</div>
        </div>
    );

    const renderStockStatus = (rowData) => {
        const pesoEstoque = getPesoEstoque(rowData);
        const palletWeight = Number(rowData.peso_paletes || 0);
        const estoqueMinimo = getEstoqueMinimo(rowData);

        if (estoqueMinimo > 0 && pesoEstoque < estoqueMinimo) {
            return <Tag severity="danger">Estoque mínimo</Tag>;
        }

        if (pesoEstoque > 0 && !rowData.spi_id) {
            return <Tag severity="warning">Sem SPI</Tag>;
        }

        if (palletWeight === pesoEstoque) {
            return <Tag severity="success">Conforme</Tag>;
        }

        return <Tag severity="danger">Não Conforme</Tag>;
    };

    const renderSpiId = (rowData) => {
        if (!rowData.spi_id) {
            return <Tag severity="warning">Sem SPI</Tag>;
        }

        return rowData.spi_id;
    };

    const getStockRowClassName = (rowData) => {
        const pesoEstoque = getPesoEstoque(rowData);
        const estoqueMinimo = getEstoqueMinimo(rowData);

        if (estoqueMinimo > 0 && pesoEstoque < estoqueMinimo) {
            return 'fio-estoque-minimo';
        }

        if (pesoEstoque > 0 && !rowData.spi_id) {
            return 'fio-sem-spi';
        }

        return '';
    };

    const renderPalletWeightIndicator = (rowData) => {
        const pesoEstoque = getPesoEstoque(rowData);
        const palletWeight = Number(rowData.peso_paletes || 0);
        const percentage = pesoEstoque > 0 ? Math.min((palletWeight / pesoEstoque) * 100, 100) : 0;
        const difference = palletWeight - pesoEstoque;
        const isBalanced = difference === 0;
        const isOver = difference > 0;
        const barStyle = isBalanced
            ? styles.palletWeightBarSuccess
            : isOver
                ? styles.palletWeightBarWarning
                : styles.palletWeightBarDanger;

        return (
            <div style={styles.palletWeight}>
                <div style={styles.palletWeightHeader}>
                    <strong>{formatNumber(palletWeight)} kg</strong>
                    <Tag severity={isBalanced ? 'success' : isOver ? 'warning' : 'danger'}>
                        {isBalanced ? 'OK' : `${isOver ? '+' : '-'}${formatNumber(Math.abs(difference))} kg`}
                    </Tag>
                </div>
                <div style={styles.palletWeightTrack}>
                    <div style={{ ...styles.palletWeightBar, ...barStyle, width: `${percentage}%` }} />
                </div>
            </div>
        );
    };

    const renderStockActions = (rowData) => (
        <Button
            className="p-button-info p-m-1"
            icon="pi pi-pencil"
            onClick={() => openStockDialog(rowData)}
        />
    );

    const renderRoutineStatus = () => {
        if (statusRotina === 'executada') {
            return <Tag severity="success">Rotina executada</Tag>;
        }

        if (erros.length > 0) {
            return <Tag severity="danger">Pendências encontradas</Tag>;
        }

        return <Tag severity="warning">Aguardando execução</Tag>;
    };

    const renderStockWeight = (rowData) => {
        return `${formatNumber(getPesoEstoque(rowData))} kg`;
    };

    const renderStockMinimum = (rowData) => {
        const estoqueMinimo = getEstoqueMinimo(rowData);

        if (estoqueMinimo <= 0) {
            return <Tag severity="info">Sem mínimo</Tag>;
        }

        return `${formatNumber(estoqueMinimo)} kg`;
    };

    const renderAutomaticRequest = (rowData) => {
        if (isSolicitacaoAutomaticaAtiva(rowData.solicita_palete_automaticamente)) {
            return <Tag severity="success">Sim</Tag>;
        }

        return <Tag severity="warning">Não</Tag>;
    };

    const renderReportNumber = (rowData, column) => {
        return Number(rowData[column.field] || 0).toLocaleString('pt-BR', {
            maximumFractionDigits: 0
        });
    };

    const renderDate = (rowData, column) => {
        return formatDateTime(rowData[column.field]);
    };

    const renderCurrency = (rowData, column) => {
        return formatCurrency(rowData[column.field]);
    };

    const renderStockDialogFooter = () => (
        <div>
            <Button
                className="p-button-secondary"
                label="Cancelar"
                onClick={closeStockDialog}
            />
            <Button
                className="p-button-success"
                label="Salvar"
                onClick={handleSubmitStock}
            />
        </div>
    );

    const renderErrorDialogFooter = () => (
        <div>
            <Button label="Fechar" icon="pi pi-times" onClick={closeErrorDialog} />
        </div>
    );

    useEffect(() => {
        if (initialRequestsLoaded.current) {
            return;
        }

        initialRequestsLoaded.current = true;
        loadDashboard(initialRequestDate.current)
            .then(() => loadRoutineErrors(initialRequestDate.current))
            .catch((error) => {
                showError(error);
            });
        handleSubmitConsumptionReport(initialRequestDate.current);
    }, [handleSubmitConsumptionReport, loadDashboard, loadRoutineErrors, showError]);

    return (
        <div style={styles.page}>
            <style>{styles.tableHighlightCss}</style>
            <Menu />
            <Toast ref={toast} />

            {loading && fios.length === 0 && (
                <Loading />
            )}

            <Card className="p-m-1" style={styles.mainCard}>
                <Dialog
                    footer={renderStockDialogFooter}
                    header="Editar Estoque"
                    onHide={closeStockDialog}
                    style={{ minWidth: '40vw' }}
                    visible={stockDialogVisible}
                >
                    <div className="p-fluid">
                        <div className="p-field">
                            <label htmlFor="titulo">Título: </label>
                            <InputText disabled value={stockForm.titulo || ''} />
                        </div>
                        <div className="p-field">
                            <label htmlFor="quantidade">Quantidade: </label>
                            <InputText
                                onChange={changeStockQuantity}
                                value={stockForm.quantidade || ''}
                            />
                        </div>
                        <div className="p-field">
                            <label htmlFor="estoque_minimo">Estoque Mínimo: </label>
                            <InputNumber
                                inputId="estoque_minimo"
                                min={0}
                                minFractionDigits={0}
                                maxFractionDigits={2}
                                mode="decimal"
                                onValueChange={changeStockMinimum}
                                value={Number(stockForm.estoque_minimo || 0)}
                            />
                        </div>
                        <div className="p-field">
                            <label htmlFor="solicita_palete_automaticamente">Solicitar Automaticamente: </label>
                            <Dropdown
                                inputId="solicita_palete_automaticamente"
                                optionLabel="label"
                                optionValue="value"
                                options={BOOLEAN_OPTIONS}
                                value={isSolicitacaoAutomaticaAtiva(stockForm.solicita_palete_automaticamente)}
                                onChange={changeAutomaticRequest}
                            />
                        </div>
                        <div className="p-field">
                            <label htmlFor="spi_id">ID SPI: </label>
                            <Dropdown
                                filter
                                optionLabel="descricao_completa"
                                optionValue="idtecfio"
                                options={fioSpiOptions}
                                placeholder="Selecione um fio"
                                value={stockForm.spi_id || null}
                                onChange={changeStockSpiId}
                            />
                        </div>
                    </div>
                </Dialog>

                <Dialog
                    header="Erros da rotina de baixa de fio"
                    visible={errorDialogVisible}
                    footer={renderErrorDialogFooter}
                    onHide={closeErrorDialog}
                    modal
                    style={styles.dialog}
                >
                    <DataTable
                        emptyMessage="Nenhum erro encontrado."
                        responsiveLayout="scroll"
                        size="small"
                        value={erros}
                    >
                        <Column field="data" header="Data" body={renderDate} />
                        <Column field="mensagem" header="Mensagem" />
                        <Column field="artigo" header="Artigo" />
                        <Column field="produto_descricao" header="Fio" />
                    </DataTable>
                </Dialog>

                <div style={styles.header}>
                    <div style={styles.headerContent}>
                        <div style={styles.headerEyebrow}>Controle operacional</div>
                        <h2 style={styles.headerTitle}>Estoque de Fios</h2>
                        <div style={styles.headerSubtitle}>{renderHeaderTitle()}</div>
                        <div style={styles.headerMeta}>
                            {renderRoutineStatus()}
                            <span style={styles.headerDate}>
                                Data de referência: <strong>{dataReferencia || '-'}</strong>
                            </span>
                        </div>
                    </div>

                    <div style={styles.headerActions}>
                        <Button
                            className="p-button-outlined"
                            icon="pi pi-refresh"
                            label="Atualizar"
                            onClick={handleRefresh}
                        />
                        <Button
                            className="p-button-outlined"
                            icon="pi pi-plus"
                            label="Solicitações"
                            onClick={() => {
                                navigate('/estoque/solicitacoes');
                            }}
                        />
                        <Button
                            className="p-button-success"
                            disabled={executing}
                            icon="pi pi-play"
                            label={executing ? 'Executando' : 'Executar rotina'}
                            onClick={() => executeRoutine()}
                        />
                        <Button
                            className="p-button-warning"
                            disabled={erros.length === 0}
                            icon="pi pi-exclamation-triangle"
                            label={`Erros (${erros.length})`}
                            onClick={openErrorDialog}
                        />
                    </div>
                </div>

                <div style={styles.summaryGrid}>
                    {renderSummaryCard(
                        'Necessidade total',
                        `${formatNumber(consumptionIndicators || pesoTotalNecessario)} kg`,
                        consumptionIndicators > 0
                            ? 'Consumo calculado pelo relatório da data buscada'
                            : 'Peso calculado por trama e urdume',
                        styles.summaryCardPrimary
                    )}
                    {renderSummaryCard('Estoque total', `${formatNumber(stockIndicators.totalStockWeight)} kg`, 'Peso real que controla o estoque', styles.summaryCardSuccess)}
                    {renderSummaryCard('Peso em paletes', `${formatNumber(stockIndicators.totalPalletWeight)} kg`, 'Conferência física que deve bater com o estoque', styles.summaryCardInfo)}
                    {renderSummaryCard('Produção importada', `${formatNumber(totalMetrosProduzidos)} m`, 'Metros considerados na rotina', styles.summaryCardInfo)}
                    {renderSummaryCard('Artigos', formatInteger(totalArtigos), 'Artigos encontrados na importação', styles.summaryCardNeutral)}
                    {renderSummaryCard('Divergência paletes', `${formatNumber(stockIndicators.totalDivergence)} kg`, `${formatInteger(stockIndicators.inconsistentItems)} fios com diferença`, styles.summaryCardWarning)}
                    {renderSummaryCard('Fios sem SPI', formatInteger(stockIndicators.itemsWithoutSpi), 'Itens com estoque sem referência SPI', styles.summaryCardWarning)}
                    {renderSummaryCard('Pendências', formatInteger(totalPendencias), 'Referências, saldos ou paletes divergentes', styles.summaryCardDanger)}
                </div>

                <div style={styles.reportSearch}>
                    <div>
                        <div style={styles.reportSearchTitle}>Relatório de consumo de fio</div>
                        <div style={styles.reportSearchDescription}>
                            Busque uma data para carregar o consumo por artigo, trama e metros produzidos.
                        </div>
                    </div>
                    <div style={styles.reportSearchActions}>
                        <InputText
                            onChange={changeConsumptionDate}
                            type="date"
                            value={consumptionDate}
                        />
                        <Button
                            className="p-button-info"
                            disabled={consumptionLoading}
                            icon="pi pi-search"
                            label={consumptionLoading ? 'Buscando' : 'Buscar consumo'}
                            onClick={() => handleSubmitConsumptionReport()}
                        />
                        <Button
                            className="p-button-warning"
                            disabled={executing}
                            icon="pi pi-cog"
                            label={executing ? 'Executando' : 'Executar rotina do consumo'}
                            onClick={() => executeRoutine(consumptionDate)}
                        />
                    </div>
                </div>

                <Accordion activeIndex={activeIndex} onTabChange={changeActiveIndex}>
                    <AccordionTab header="Estoque de Fios">
                        <div style={styles.stockFilters}>
                            <div style={styles.stockFilterField}>
                                <label htmlFor="stock_filter" style={styles.stockFilterLabel}>Filtro</label>
                                <Dropdown
                                    inputId="stock_filter"
                                    optionLabel="label"
                                    optionValue="value"
                                    options={STOCK_FILTER_OPTIONS}
                                    value={stockFilter}
                                    onChange={changeStockFilter}
                                />
                            </div>
                            <Tag severity="info">
                                {formatInteger(fiosFiltrados.length)} de {formatInteger(fios.length)} registros
                            </Tag>
                        </div>
                        <DataTable
                            emptyMessage="Nenhum registro encontrado."
                            loading={loading}
                            paginator
                            rows={15}
                            rowHover
                            rowClassName={getStockRowClassName}
                            responsiveLayout="scroll"
                            size="small"
                            value={fiosFiltrados}
                        >
                            <Column header="ID SPI" field="spi_id" body={renderSpiId} sortable />
                            <Column header="Título" field="titulo" sortable />
                            <Column header="UN" field="un" sortable />
                            <Column header="Peso Estoque" field="quantidade" body={renderStockWeight} sortable />
                            <Column header="Estoque Mínimo" field="estoque_minimo" body={renderStockMinimum} sortable />
                            <Column header="Solicitação Auto." field="solicita_palete_automaticamente" body={renderAutomaticRequest} sortable />
                            <Column header="Peso Paletes" field="peso_paletes" body={renderPalletWeightIndicator} sortable />
                            <Column header="Valor" field="valor_unico" body={renderCurrency} sortable />
                            <Column header="Situação" body={renderStockStatus} />
                            <Column header="Ações" body={renderStockActions} />
                        </DataTable>
                    </AccordionTab>

                    <AccordionTab header="Consumo por artigo e grupo de máquinas">
                        <DataTable
                            emptyMessage="Busque uma data para visualizar o consumo."
                            loading={consumptionLoading}
                            paginator
                            rows={15}
                            rowHover
                            responsiveLayout="scroll"
                            size="small"
                            value={consumptionReport.artigos}
                        >
                            <Column field="artigo" header="Artigo" sortable />
                            <Column field="trama1" header="Trama" sortable />
                            <Column field="urdume1" header="Urdume" sortable />
                            <Column field="producao_picanol" header="Picanol" body={renderReportNumber} sortable />
                            <Column field="consumotrama1_picanol" header="Consumo Trama" body={renderReportNumber} sortable />
                            <Column field="consumourdume1_picanol" header="Consumo Urdume" body={renderReportNumber} sortable />
                            <Column field="producao_toyota" header="Toyota" body={renderReportNumber} sortable />
                            <Column field="consumotrama1_toyota" header="Consumo Trama" body={renderReportNumber} sortable />
                            <Column field="consumourdume1_toyota" header="Consumo Urdume" body={renderReportNumber} sortable />
                        </DataTable>
                    </AccordionTab>

                    <AccordionTab header="Consumo por trama">
                        <DataTable
                            emptyMessage="Busque uma data para visualizar o consumo por trama."
                            loading={consumptionLoading}
                            paginator
                            rows={15}
                            rowHover
                            responsiveLayout="scroll"
                            size="small"
                            value={consumptionReport.tramas}
                        >
                            <Column field="trama" header="Trama" sortable />
                            <Column field="consumo" header="Consumo" body={renderReportNumber} sortable />
                        </DataTable>
                    </AccordionTab>

                    <AccordionTab header="Metros total">
                        <DataTable
                            emptyMessage="Busque uma data para visualizar os metros por artigo."
                            loading={consumptionLoading}
                            paginator
                            rows={15}
                            rowHover
                            responsiveLayout="scroll"
                            size="small"
                            value={consumptionReport.artigo_maquinas}
                        >
                            <Column field="artigo" header="Artigo" body={renderReportNumber} sortable />
                            <Column field="picanol" header="Picanol" body={renderReportNumber} sortable />
                            <Column field="toyota" header="Toyota" body={renderReportNumber} sortable />
                            <Column field="total" header="Total" body={renderReportNumber} sortable />
                        </DataTable>
                    </AccordionTab>
                </Accordion>
            </Card>
        </div>
    );
};

export default ControleEstoqueFio;
